'use client';

import { useFiltersContext } from '@components/FilterPage/Context/useFiltersContext';
import { AccordionItem, AccordionItemProps } from '@coolblue-development/freddy';
import selectMoreLessFiltersTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/selectMoreLessFiltersTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import { FilterCategories, FilterOptions } from '@server/careers-backend/search';
import React from 'react';

import { FilterFacetCheckboxItem } from './FilterFacetCheckboxItem';
import { FilterFacetNumberItem } from './FilterFacetNumberItem';

type Props = {
  filterCategory: FilterCategories;
  filterOptions: FilterOptions;
  isAccordionVersion: boolean;
  isBoolean?: boolean;
};

export const FilterFacetItem = ({
  filterCategory,
  filterOptions,
  isAccordionVersion,
  isBoolean = false
}: Props) => {
  const { t } = useTranslation('filterPage.filterFacets');
  const translatedKey = t(`${filterCategory}.label`);

  const { getSelectedFilterOptions, setFilter } = useFiltersContext();

  const { trackEvent } = useAnalytics();

  // Skip rendering if translation key indicates no title or if there are no values
  if (
    translatedKey.includes('filterPage.filterFacets') ||
    !filterOptions ||
    (Array.isArray(filterOptions)
      ? filterOptions.length === 0
      : !('min' in filterOptions && 'max' in filterOptions))
  ) {
    return undefined;
  }

  const FilterWrapper = (props: AccordionItemProps) =>
    isAccordionVersion ? (
      <AccordionItem
        {...props}
        onToggle={e =>
          trackEvent(
            selectMoreLessFiltersTrigger({
              sub_feature: 'filter_overlay',
              property_name: filterCategory,
              method: (e.nativeEvent as ToggleEvent).newState === 'open' ? 'expand' : 'collapse'
            })
          )
        }
      />
    ) : (
      <React.Fragment>{props.children}</React.Fragment>
    );

  return (
    <FilterWrapper key={filterCategory} title={isAccordionVersion ? translatedKey : undefined}>
      {Array.isArray(filterOptions) ? (
        <FilterFacetCheckboxItem
          label={isAccordionVersion ? undefined : translatedKey}
          filterCategory={filterCategory}
          filterOptions={filterOptions}
          isBoolean={isBoolean}
          isMobileVersion={isAccordionVersion}
          selectedFilterOptions={getSelectedFilterOptions(filterCategory)}
          onChange={values => {
            setFilter(filterCategory, values);
          }}
        />
      ) : (
        <FilterFacetNumberItem
          label={isAccordionVersion ? undefined : translatedKey}
          filterCategory={filterCategory}
          minOption={filterOptions.min}
          maxOption={filterOptions.max}
          isMobileVersion={isAccordionVersion}
        />
      )}
    </FilterWrapper>
  );
};
